import React, { useEffect, useState } from "react";
import { Plus, Trash2, Clock, Euro, Users } from "lucide-react";
import { api } from "../../../lib/api";
import Field from "../components/Field";

/**
 * Services tab — CRUD on `db.services`.
 *
 * Extracted from BusinessDashboardShell.jsx (iter28 refactor step 2).
 * No logic, UX or API change — only the file location.
 *
 * Capability: gated by `services:view` (read) at the shell level. Mutations
 * are still backend-enforced; this component does not duplicate auth checks.
 */
export default function Services({ businessId }) {
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [bizCategory, setBizCategory] = useState("");
  const [form, setForm] = useState({ name: "", duration_min: 30, price: 0, description: "", buffer_min: 0, max_concurrent: 1, staff_ids: [], deposit_type: "none", deposit_value: 0, category: "" });
  const [editing, setEditing] = useState(null);

  async function load() {
    const [a, s] = await Promise.all([
      api.get(`/businesses/${businessId}`),
      api.get(`/businesses/${businessId}/staff`),
    ]);
    setItems(a.data.services || []);
    setBizCategory(a.data.category || "");
    setStaff(s.data || []);
  }
  useEffect(() => { load(); }, [businessId]);

  // Preset suggestions per business category — empty list disables datalist
  const CATEGORY_PRESETS = {
    estetica: ["Capelli", "Viso", "Corpo", "Mani & Piedi", "Epilazione", "Make-up", "Massaggi"],
    parrucchiere: ["Capelli", "Barba", "Colore", "Trattamenti", "Sposa"],
    palestra: ["Personal Training", "Corsi di gruppo", "Valutazione", "Consulenze nutrizionali"],
    studio: ["Visite", "Trattamenti", "Consulenze", "Esami"],
  };
  const presets = CATEGORY_PRESETS[bizCategory] || [];

  async function submit(e) {
    e.preventDefault();
    const payload = { ...form, duration_min: Number(form.duration_min), price: Number(form.price), buffer_min: Number(form.buffer_min), max_concurrent: Math.max(1, Number(form.max_concurrent) || 1), deposit_value: Number(form.deposit_value) || 0, category: (form.category || "").trim() };
    if (editing) {
      await api.put(`/services/${editing}`, payload);
      setEditing(null);
    } else {
      await api.post(`/businesses/${businessId}/services`, payload);
    }
    setForm({ name: "", duration_min: 30, price: 0, description: "", buffer_min: 0, max_concurrent: 1, staff_ids: [], deposit_type: "none", deposit_value: 0, category: "" });
    load();
  }

  async function del(id) {
    if (!window.confirm("Eliminare il servizio?")) return;
    await api.delete(`/services/${id}`);
    load();
  }

  function toggleStaff(id) {
    setForm((f) => ({
      ...f,
      staff_ids: f.staff_ids.includes(id) ? f.staff_ids.filter((x) => x !== id) : [...f.staff_ids, id],
    }));
  }

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <h3 className="font-display text-xl font-medium mb-3">{editing ? "Modifica servizio" : "Nuovo servizio"}</h3>
        <form onSubmit={submit} className="card-soft p-5 space-y-3" data-testid="service-form">
          <Field label="Nome"><input required className="input-soft" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="svc-name" /></Field>
          <Field label="Categoria (opzionale)">
            <input
              className="input-soft"
              list={`svc-cat-presets-${businessId}`}
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder={presets[0] ? `Es. ${presets[0]}` : "Es. Capelli, Barba, Colore..."}
              data-testid="svc-category"
            />
            {presets.length > 0 && (
              <datalist id={`svc-cat-presets-${businessId}`}>
                {presets.map((p) => <option key={p} value={p} />)}
              </datalist>
            )}
            <div className="text-xs text-zinc-500 mt-1 dark:text-zinc-400">Raggruppa servizi nella vista cliente. Lascia vuoto se non vuoi categorie.</div>
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Durata (min)"><input type="number" min={5} required className="input-soft" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} data-testid="svc-duration" /></Field>
            <Field label="Buffer (min)"><input type="number" min={0} className="input-soft" value={form.buffer_min} onChange={(e) => setForm({ ...form, buffer_min: e.target.value })} data-testid="svc-buffer" /></Field>
            <Field label="Prezzo (€)"><input type="number" min={0} step="0.01" required className="input-soft" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="svc-price" /></Field>
          </div>
          <Field label="Posti per slot">
            <input type="number" min={1} max={100} required className="input-soft" value={form.max_concurrent} onChange={(e) => setForm({ ...form, max_concurrent: e.target.value })} data-testid="svc-capacity" />
            <div className="text-xs text-zinc-500 mt-1 dark:text-zinc-400">1 = servizio individuale (es. Taglio, Visita). &gt;1 = corso/gruppo (es. Personal training di gruppo: 4).</div>
          </Field>
          <Field label="Caparra online (Stripe)">
            <div className="flex gap-2">
              <select className="input-soft flex-1" value={form.deposit_type} onChange={(e) => setForm({ ...form, deposit_type: e.target.value })} data-testid="svc-deposit-type">
                <option value="none">Nessuna</option>
                <option value="fixed">Importo fisso (€)</option>
                <option value="percent">Percentuale del prezzo (%)</option>
              </select>
              {form.deposit_type !== "none" && (
                <input type="number" min={0} step="0.01" className="input-soft w-32" value={form.deposit_value} onChange={(e) => setForm({ ...form, deposit_value: e.target.value })} placeholder={form.deposit_type === "fixed" ? "10.00" : "30"} data-testid="svc-deposit-value" />
              )}
            </div>
            <div className="text-xs text-zinc-500 mt-1 dark:text-zinc-400">Opzionale. Quando attiva, il cliente versa la caparra su Stripe prima di confermare la prenotazione (riduce il tasso no-show).</div>
          </Field>
          <Field label="Descrizione"><textarea rows={2} className="input-soft h-auto py-3" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          {staff.length > 0 && (
            <Field label="Staff assegnati (lascia vuoto = tutto il team)">
              <div className="flex flex-wrap gap-2" data-testid="svc-staff-list">
                {staff.map((s) => {
                  const active = form.staff_ids.includes(s.id);
                  return (
                    <button key={s.id} type="button" onClick={() => toggleStaff(s.id)}
                      className={`h-9 px-3 rounded-full text-sm font-medium border transition ${active ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`}
                      data-testid={`svc-staff-${s.id}`}>
                      <span className="inline-block h-2 w-2 rounded-full mr-2" style={{ backgroundColor: s.color || "#2563EB" }} />
                      {s.name}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}
          <button className="btn-accent w-full" data-testid="svc-submit">{editing ? "Aggiorna" : (<><Plus className="h-4 w-4 mr-2" />Aggiungi</>)}</button>
          {editing && <button type="button" onClick={() => { setEditing(null); setForm({ name: "", duration_min: 30, price: 0, description: "", buffer_min: 0, max_concurrent: 1, staff_ids: [], deposit_type: "none", deposit_value: 0, category: "" }); }} className="text-sm text-zinc-500 w-full dark:text-zinc-400">Annulla modifica</button>}
        </form>
      </div>
      <div>
        <h3 className="font-display text-xl font-medium mb-3">Servizi ({items.length})</h3>
        <div className="space-y-3">
          {items.map((s) => (
            <div key={s.id} className="card-soft p-5 flex items-center gap-3" data-testid={`service-row-${s.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{s.name}</div>
                <div className="text-xs text-zinc-500 flex gap-3 mt-1 flex-wrap dark:text-zinc-400">
                  {s.category && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700 font-medium text-[10px] uppercase tracking-wider dark:bg-zinc-800 dark:text-zinc-300" data-testid={`svc-cat-${s.id}`}>
                      {s.category}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{s.duration_min}+{s.buffer_min || 0}min</span>
                  <span className="inline-flex items-center gap-1"><Euro className="h-3 w-3" />{s.price}</span>
                  {(s.max_concurrent || 1) > 1 && (
                    <span className="inline-flex items-center gap-1 text-blue-600 font-medium">
                      <Users className="h-3 w-3" />{s.max_concurrent} posti/slot
                    </span>
                  )}
                  {(s.staff_ids || []).length > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {(s.staff_ids || []).map((id) => staff.find((st) => st.id === id)?.name).filter(Boolean).join(", ") || `${s.staff_ids.length} staff`}
                    </span>
                  )}
                </div>
              </div>
              <button onClick={() => { setEditing(s.id); setForm({ name: s.name, duration_min: s.duration_min, price: s.price, description: s.description || "", buffer_min: s.buffer_min || 0, max_concurrent: s.max_concurrent || 1, staff_ids: s.staff_ids || [], deposit_type: s.deposit_type || "none", deposit_value: s.deposit_value || 0, category: s.category || "" }); }} className="text-xs text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100" data-testid={`svc-edit-${s.id}`}>Modifica</button>
              <button onClick={() => del(s.id)} className="h-9 w-9 rounded-full hover:bg-red-50 hover:text-red-600 flex items-center justify-center text-zinc-400 dark:text-zinc-500 dark:bg-red-950/40" data-testid={`svc-del-${s.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {items.length === 0 && <div className="text-sm text-zinc-500 dark:text-zinc-400">Nessun servizio.</div>}
        </div>
      </div>
    </div>
  );
}
